import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";

export type ApparatusDeviceRead = components["schemas"]["ApparatusDeviceRead"];
export type ApparatusMetricRead = components["schemas"]["ApparatusMetricRead"];
export type ApparatusReadingsRead =
  components["schemas"]["ApparatusReadingsRead"];
export type UnclaimedThingRead = components["schemas"]["UnclaimedThingRead"];

// Both endpoints are respai_admin-only and department-scoped server-side (an
// admin re-targets them with the X-RespAi-Department-ID header), so the cache
// keys carry the active department or two tenants collide on one entry — the
// same rule gateway-fleet follows.

export function useApparatusDevices() {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["apparatus", deptId],
    queryFn: () => api.get<ApparatusDeviceRead[]>("/apparatus"),
  });
}

/**
 * Things seen on the wire that no department has claimed (RES-144).
 *
 * The one apparatus query with **no department in its cache key**, and the
 * exception is the whole point: the backing table has no `department_id` at all
 * (an unclaimed thing belongs to no tenant — see migration 0026), so the
 * response is identical whatever the switcher is set to. Keying it by department
 * would cache the same global list once per tenant and refetch it on every
 * switch, implying a per-department view that does not exist.
 */
export function useUnclaimedThings() {
  return useQuery({
    queryKey: ["apparatus-unclaimed"],
    queryFn: () => api.get<UnclaimedThingRead[]>("/apparatus/unclaimed"),
  });
}

export function useApparatusReadings(apparatusId: string | null | undefined) {
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["apparatus-readings", deptId, apparatusId],
    // A device row can exist with a null apparatus_id (sensor claimed, not yet
    // bound to a truck). There is nothing to fetch readings for in that case.
    enabled: Boolean(apparatusId),
    queryFn: () =>
      api.get<ApparatusReadingsRead>(
        `/apparatus/${encodeURIComponent(apparatusId as string)}/readings`,
      ),
  });
}
